import { ChevronLeft, ChevronRight } from "lucide-react";
import React from "react";

export const PaginationButtons = ({
	total,
	currentPage,
	onPageChange,
	itemsPerPage,
	className = "mt-4",
}) => {
	const pageCount = Math.ceil(total / itemsPerPage);

	if (pageCount <= 1) return null;

	// Determine which pages to show (show at most 5 pages + first/last)
	const getVisiblePages = () => {
		// Always show max 7 items (including prev/next)
		const maxVisiblePages = 5;
		let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
		const endPage = Math.min(pageCount, startPage + maxVisiblePages - 1);

		// Adjust if we're near the end
		if (endPage - startPage < maxVisiblePages - 1) {
			startPage = Math.max(1, endPage - maxVisiblePages + 1);
		}

		const pages = [];

		// Add first page if not already in range
		if (startPage > 1) {
			pages.push(1);
			if (startPage > 2) {
				pages.push("ellipsis-start");
			}
		}

		// Add visible page numbers
		for (let i = startPage; i <= endPage; i++) {
			pages.push(i);
		}

		// Add last page if not already in range
		if (endPage < pageCount) {
			if (endPage < pageCount - 1) {
				pages.push("ellipsis-end");
			}
			pages.push(pageCount);
		}

		return pages;
	};

	const visiblePages = getVisiblePages();
	const buttonBaseClass =
		"no-hover-lift h-7 min-w-7 items-center justify-center rounded border px-1.5 text-xs transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] sm:h-8 sm:min-w-8 sm:px-2 sm:text-sm";
	const inactiveButtonClass =
		"border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] hover:border-[var(--color-border-hover)] hover:bg-[var(--color-bg-hover)]";
	const disabledButtonClass =
		"cursor-not-allowed border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-text-tertiary)] opacity-50";
	const getButtonClass = (hideOnCompact = false) =>
		`${hideOnCompact ? "hidden sm:inline-flex" : "inline-flex"} ${buttonBaseClass}`;

	const renderPageButton = (page, index) => {
		// Handle ellipsis
		if (typeof page === "string" && page.includes("ellipsis")) {
			return (
				<span
					key={page}
					className="hidden h-7 items-center px-1.5 text-xs text-[var(--color-text-secondary)] sm:inline-flex sm:h-8 sm:px-2 sm:text-sm"
				>
					…
				</span>
			);
		}

		const isActive = currentPage === page;
		const hideOnCompact = !isActive && Math.abs(Number(page) - currentPage) > 1;

		return (
			<button
				key={`page-${page}-${index}`}
				type="button"
				onClick={() => onPageChange(page)}
				className={`${getButtonClass(hideOnCompact)} ${
					isActive
						? "border-[var(--color-accent-primary)] bg-[var(--color-accent-primary)] font-medium text-[var(--color-action-text)]"
						: inactiveButtonClass
				}`}
				aria-current={isActive ? "page" : undefined}
			>
				{page}
			</button>
		);
	};

	return (
		<div
			className={`flex min-w-0 items-center justify-start gap-1 sm:gap-1.5 ${className}`}
		>
			<button
				type="button"
				onClick={() => onPageChange(currentPage - 1)}
				disabled={currentPage <= 1}
				className={`${getButtonClass()} ${
					currentPage <= 1 ? disabledButtonClass : inactiveButtonClass
				}`}
				aria-label="Previous page"
			>
				<ChevronLeft className="h-4 w-4" />
			</button>
			{visiblePages.map(renderPageButton)}
			<button
				type="button"
				onClick={() => onPageChange(currentPage + 1)}
				disabled={currentPage >= pageCount}
				className={`${getButtonClass()} ${
					currentPage >= pageCount ? disabledButtonClass : inactiveButtonClass
				}`}
				aria-label="Next page"
			>
				<ChevronRight className="h-4 w-4" />
			</button>
		</div>
	);
};
